import axios, { type AxiosRequestConfig } from 'axios';
import { useContext, useMemo } from 'react';
import { config } from '../../../modules/config';
import { AuthContext } from '../../../modules/context/authContext';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import type {
  CreateLockGroupPayload,
  LockGroupRecord,
  UpdateLockGroupPayload,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';

/**
 * Admin CRUD for lock groups against `v3/locks/lock-groups`, plus the membership
 * sub-routes for adding/removing locks and rooms.
 *
 * Thin axios-backed service hook (same shape as useLocks): Cognito token +
 * `property-id` header, unwraps `data.result`. Note that create/update accept
 * `lockUuids`/`roomUuids` which OVERWRITE the membership set; the dedicated
 * add/remove routes are for incremental changes.
 */
export interface LockGroupsService {
  list: () => Promise<LockGroupRecord[]>;
  get: (uuid: string) => Promise<LockGroupRecord>;
  create: (payload: CreateLockGroupPayload) => Promise<LockGroupRecord>;
  update: (uuid: string, payload: UpdateLockGroupPayload) => Promise<LockGroupRecord>;
  remove: (uuid: string) => Promise<void>;
  addLocks: (uuid: string, lockUuids: string[]) => Promise<LockGroupRecord>;
  removeLocks: (uuid: string, lockUuids: string[]) => Promise<LockGroupRecord>;
  addRooms: (uuid: string, roomUuids: string[]) => Promise<LockGroupRecord>;
  removeRooms: (uuid: string, roomUuids: string[]) => Promise<LockGroupRecord>;
}

export const useLockGroups = (): LockGroupsService => {
  const { credentialsInfo } = useContext(AuthContext) || {};
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const token = credentialsInfo?.token;
  const propertyHeaderId = selectedProperty?.property.uuid;

  return useMemo<LockGroupsService>(() => {
    const request = async <R>(reqConfig: AxiosRequestConfig): Promise<R> => {
      const headers: Record<string, string> = {};
      if (token) headers.authorization = token;
      if (propertyHeaderId) headers['property-id'] = propertyHeaderId;

      const { data } = await axios.request<SuccessEnvelop<R>>({
        baseURL: config.bookingEngine.endpoint,
        ...reqConfig,
        headers: { ...headers, ...reqConfig.headers },
      });
      return data.result as R;
    };

    return {
      list: () =>
        request<LockGroupRecord[]>({ url: endpoints.LOCK_GROUPS, method: 'get' }),

      get: (uuid) =>
        request<LockGroupRecord>({
          url: `${endpoints.LOCK_GROUPS}/${uuid}`,
          method: 'get',
        }),

      create: (payload) =>
        request<LockGroupRecord>({
          url: endpoints.LOCK_GROUPS,
          method: 'post',
          data: payload,
        }),

      update: (uuid, payload) =>
        request<LockGroupRecord>({
          url: `${endpoints.LOCK_GROUPS}/${uuid}`,
          method: 'patch',
          data: payload,
        }),

      remove: (uuid) =>
        request<void>({ url: `${endpoints.LOCK_GROUPS}/${uuid}`, method: 'delete' }),

      addLocks: (uuid, lockUuids) =>
        request<LockGroupRecord>({
          url: `${endpoints.LOCK_GROUPS}/${uuid}/locks`,
          method: 'post',
          data: { lockUuids },
        }),

      removeLocks: (uuid, lockUuids) =>
        request<LockGroupRecord>({
          url: `${endpoints.LOCK_GROUPS}/${uuid}/locks`,
          method: 'delete',
          data: { lockUuids },
        }),

      addRooms: (uuid, roomUuids) =>
        request<LockGroupRecord>({
          url: `${endpoints.LOCK_GROUPS}/${uuid}/rooms`,
          method: 'post',
          data: { roomUuids },
        }),

      removeRooms: (uuid, roomUuids) =>
        request<LockGroupRecord>({
          url: `${endpoints.LOCK_GROUPS}/${uuid}/rooms`,
          method: 'delete',
          data: { roomUuids },
        }),
    };
  }, [token, propertyHeaderId]);
};
